<template>
  <!-- _________________________ LOGIN _________________________ -->
  <div>

    <h1 class="page-title mt-5 bold-text">
      {{ $t('loginTitle') }}
    </h1>
    <h2 class="page-subtitle mt-3">
      {{ $t('loginSubtitle') }}
    </h2>

    <form class="mt-4" @submit.stop.prevent="handleSubmit">

      <b-alert :show="ui.showError" variant="danger">
        <div v-html="ui.showErrorMessage"></div>
        <a v-if="ui.showValidateAccountError" href="#" v-on:click="resendValidateAccountMail">
          {{ $t('account.not.validated.resent.error') }}
        </a>
      </b-alert>

      <b-alert :show="ui.showResendValidateAccountMailSuccess" variant="success">
        {{ $t('ResendValidateAccountMail.success') }}
      </b-alert>

      <b-form-group
        :label="$t('loginFirstCredential')"
        label-for="username-input">
        <b-form-input
          id="username-input"
          v-model="form.username.value"
          required
          type="email"
          @input="updateMail"
          @keydown="hideError">
        </b-form-input>
      </b-form-group>

      <b-form-group
        :label="$t('loginSecondCredential')"
        label-for="password-input">
        <b-form-input
          id="password-input"
          v-model="form.password.value"
          required
          type="password"
          @keydown="hideError">
        </b-form-input>
      </b-form-group>

      <div class="mt-4">
        <b-button class="btn-block bold-text" type="submit" variant="secondary">{{ $t('LogInButtonText') }}</b-button>
      </div>

    </form>

    <div class="row justify-content-between">
      <div class="col">
        <b-form-checkbox
          id="remember_me"
          v-model="form.rememberMe.value"
          class="mt-4 remember-me">
          {{ $t('Remember.me') }}
        </b-form-checkbox>
      </div>
      <div class=" col mt-4 text-right">
        <b-link class="forgot-password-link" v-on:click="showForgotPasswordView">{{ $t('forgotPasswordText') }}</b-link>
      </div>
    </div>

    <div class="mt-4 text-center" v-if="canSignUp">
      <span>{{ $t('No.account') }}</span>
      <span id="register-link" class="page-title" v-on:click="showRegister">{{ $t('Register.here') }}</span>
    </div>

  </div>
</template>

<script>

import {mapActions} from 'vuex'
import api from '../../../services/api'

export default {

  name: 'LoginComponent',
  props: {
    error: {
      type: String,
      required: false,
      default: null
    },
    canSignUp: {
      required: true
    }
  },

  data() {
    return {
      form: {
        username: {
          value: null
        },
        password: {
          value: null
        },
        rememberMe: {
          value: false
        },
      },
      ui: {
        showError: false,
        showErrorMessage: this.$i18n.t('internal.server.error'),
        showValidateAccountError: false,
        showResendValidateAccountMailSuccess: false
      }
    }
  },

  emits: ['showForgotPasswordView', 'showSmsCodeView', 'showRegister', 'inputMail'],

  watch: {
    error: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          this.ui.showErrorMessage = newValue
          this.ui.showError = true
        }
      },
    },
  },
  mounted() {
  },

  methods: {
    updateMail() {
      this.$emit('inputMail', this.form.username.value)
    },
    ...mapActions(['LogIn', 'setRememberMe']),

    showRegister() {
      this.$emit('showRegister')
    },

    async handleSubmit() {
      let data = {
        username: this.form.username.value.trim(),
        password: this.form.password.value.trim()
      }

      this.setRememberMe(this.form.rememberMe.value)
      if (this.form.rememberMe.value) {
        data['remember-me'] = true
      }

      let formData = new FormData()
      formData.append('username', data['username'])
      formData.append('password', data['password'])

      await api({
        method: 'POST',
        url: '/authentication/beforeLogin',
        data: formData
      })
        .then(async (response) => {
          let displaySmsCodeValidation = response.data.displayCodeValidation
          if (displaySmsCodeValidation) {
            // code sms
            let phoneNumber = Object(response.data)['phoneNumber']
            this.showSMSCodeView(data['username'], data['password'], phoneNumber)
          } else {
            // login
            await this.LogIn(data)
              .then(async () => {
                this.ui.showError = false
                this.goToHome()
              })
              .catch((error) => {
                if (error.toString() === 'Locked') {
                  this.ui.showErrorMessage = this.$i18n.t('Profile.forbidden.error')
                  this.ui.showError = true
                }
              })
          }
        })
        .catch((error) => {
          if (error.response.status === 401) {
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }
          if (error.response.status === 403) {
            this.ui.showErrorMessage = this.$i18n.t(error.response.data.message)
            this.ui.showError = true
            if (error.response.data.message === 'account.not.validated.error') {
              this.ui.showValidateAccountError = true
            }
          }
          if (error.response.status === 423) {
            this.ui.showErrorMessage = this.$i18n.t(error.response.data.message)
            this.ui.showError = true
          }
          if (error.response.status === 500) {
            this.ui.showErrorMessage = this.$i18n.t('internal.server.error')
            this.ui.showError = true
          }
        })
    },

    resendValidateAccountMail() {
      this.hideError()

      let formData = new FormData()
      formData.append('username', this.form.username.value.trim())

      api({
        method: 'POST',
        url: `/authentication/resendValidateAccountMail`,
        data: formData
      })
        .then(() => {
          this.ui.showResendValidateAccountMailSuccess = true
        })
        .catch((error) => {
          if (error.response.status === 401) {
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }
          if (error.response.status === 500) {
            this.ui.showErrorMessage = this.$i18n.t('internal.server.error')
            this.ui.showError = true
          }
        })
    },

    // showRegister() {
    //   this.$emit('showRegister')
    // },

    showForgotPasswordView() {
      this.$emit('showForgotPasswordView')
    },

    showSmsCodeView(username, password, phoneNumber) {
      this.$emit('showSmsCodeView', username, password, phoneNumber)
    },

    hideError() {
      this.ui.showError = false
      this.ui.showErrorMessage = null
      this.ui.showValidateAccountError = false
    },

    hideMessages() {
      this.ui.showResendValidateAccountMailSuccess = false
      this.hideError()
    },

    goToHome() {
     if(this.$store.getters.isAgentImmo){
        this.$router.push({
          name: 'DashboardAgentImmo'
        })
      } else {
        this.$router.push({
          name: 'Dashboard'
        })
      }
    }

  }

}

</script>

<style scoped>

#register-link {
  font-size: var(--title-level-two-font-size);
  font-family: "Montserrat-Bold", "Arial", sans-serif;
  text-decoration: underline;
  cursor: pointer;
}

#register-link:hover {
  color: var(--secondary-text-color);
}

</style>



// WEBPACK FOOTER //
// src/views/auth/components/LoginComponent.vue